import { BattlePassService } from './BattlePassService';
import { config, shouldLog, updateDomainConfig } from '../config/config';

interface TokenMessage {
  type: 'SET_TOKEN';
  token: string;
}

interface DomainConfigMessage {
  type?: 'SET_DOMAIN_CONFIG';
  iconBaseUrl?: string;
  apiUrlPrefix?: string;
  publicUrlEncryptKeys?: string[];
  // 父層可能發送的額外欄位
  isIframeReady?: boolean;
  hasSource?: boolean;
}

export class AuthManager {
  private static instance: AuthManager;
  private token: string | null = null;
  private userId: string | null = null;
  private readonly parentOrigin: string | null;
  private battlePassService: BattlePassService | null = null;
  private tokenChangeCallbacks: ((_token: string | null) => void)[] = [];
  private readinessSignaled: boolean = false;
  private retryTimer: number | null = null;
  private retryCount: number = 0;
  private readonly maxRetries: number = 5;
  private readonly retryInterval: number = 2000;
  private readonly timeoutDuration: number = 30000; // 30秒超時
  private timeoutTimer: number | null = null;
  private domContentLoadedHandler: (() => void) | null = null;
  private iframeReadySendCount: number = 0; // 追蹤發送 IFRAME_READY 的次數

  private constructor() {    
    // 根據規範使用 document.referrer 推斷 parent origin
    this.parentOrigin = this.inferParentOrigin();
    
    // 🔴 強制輸出，用於 debug
    console.log('[AuthManager] 🔧 初始化:', {
      parentOrigin: this.parentOrigin,
      referrer: document.referrer,
      isInIframe: window.parent !== window,
    });
    
    if (shouldLog('info')) {
      console.log('[AuthManager] shouldLog(info) 通過');
    } else {
      console.log('[AuthManager] shouldLog(info) 未通過');
    }
    
    this.setupMessageListener();

    // 多重時機發送準備訊號，確保父層能收到
    this.setupReadinessSignaling();
  }

  public static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private inferParentOrigin(): string | null {
    try {
      // 優先使用 document.referrer
      if (document.referrer) {
        const origin = new URL(document.referrer).origin;
        if (shouldLog('info')) {
          console.log(`[AuthManager] 從 document.referrer 推斷 parent origin: ${origin}`);
        }
        return origin;
      }

      // 如果 referrer 為空，嘗試從 URL 參數推斷
      if (window.location.search.includes('parent_origin=')) {
        const urlParams = new URLSearchParams(window.location.search);
        const parentOrigin = urlParams.get('parent_origin');
        if (parentOrigin) {
          if (shouldLog('info')) {
            console.log(`[AuthManager] 從 URL 參數推斷 parent origin: ${parentOrigin}`);
          }
          return parentOrigin;
        }
      }

      // 嘗試從 window.name 推斷（某些 iframe 設置會使用這個）
      if (window.name && window.name.includes('://')) {
        try {
          const origin = new URL(window.name).origin;
          if (shouldLog('info')) {
            console.log(`[AuthManager] 從 window.name 推斷 parent origin: ${origin}`);
          }
          return origin;
        } catch {
          // window.name 不是有效的 URL，忽略
        }
      }

      // 開發環境的 fallback
      if (config.environment === 'development') {
        if (shouldLog('warn')) {
          console.warn('[AuthManager] 開發環境：無法推斷 parent origin，將使用 * 作為 fallback');
        }
        return '*';
      }

      if (shouldLog('warn')) {
        console.warn('[AuthManager] 無法推斷 parent origin');
      }
      return null;
    } catch (error) {
      if (shouldLog('warn')) {
        console.warn('[AuthManager] 推斷 parent origin 時發生錯誤:', error);
      }
      return null;
    }
  }

  private setupReadinessSignaling(): void {
    // 設置超時機制
    this.setupTimeout();

    // 1. 立即發送（如果 DOM 已準備好）
    if (document.readyState !== 'loading') {
      this.signalReadiness();
    }

    // 2. DOMContentLoaded 時發送
    if (document.readyState === 'loading') {
      this.domContentLoadedHandler = () => {
        this.signalReadiness();
      };
      document.addEventListener('DOMContentLoaded', this.domContentLoadedHandler);
    }

    // 3. 延遲發送（確保父層有時間設置監聽器）
    setTimeout(() => {
      this.signalReadiness();
    }, 100);

    // 4. 如果沒有收到 token，開始重試機制
    this.startRetryMechanism();
  }

  private setupTimeout(): void {
    // 設置總超時時間
    this.timeoutTimer = window.setTimeout(() => {
      if (this.token === null) {
        if (shouldLog('warn')) {
          console.warn('[AuthManager] 超時：30秒內未收到 token，停止重試');
        }
        this.stopRetryMechanism();
      }
    }, this.timeoutDuration);
  }

  private signalReadiness(): void {
    // 避免重複發送（只有在已經發送過且已收到 token 時才跳過）
    if (this.readinessSignaled && this.token !== null) {
      return;
    }

    try {
      // 增加發送計數
      this.iframeReadySendCount++;
      
      // 1. 先對 '*' 廣播一次，確保即使 parentOrigin 為 null 也能發送
      window.parent.postMessage({ type: 'IFRAME_READY' }, '*');

      // 🔴 強制輸出
      console.log(`[AuthManager] 📤 發送 IFRAME_READY (第 ${this.iframeReadySendCount} 次，廣播至 *)`);

      if (shouldLog('info')) {
        console.log(`[AuthManager] 第 ${this.iframeReadySendCount} 次發送準備就緒訊號（廣播至 *）`);
      }

      // 2. 如果有特定的 parentOrigin 且不等於 '*'，再發送一次指定 origin
      if (this.parentOrigin && this.parentOrigin !== '*') {
        window.parent.postMessage({ type: 'IFRAME_READY' }, this.parentOrigin);
        
        if (shouldLog('info')) {
          console.log(`[AuthManager] 第 ${this.iframeReadySendCount} 次發送準備就緒訊號（指定 origin: ${this.parentOrigin}）`);
        }
      }

      this.readinessSignaled = true;
      
    } catch (error) {
      if (shouldLog('error')) {
        console.error('[AuthManager] 發送準備就緒訊號失敗:', error);
      }
    }
  }

  private startRetryMechanism(): void {
    // 如果 3 秒後還沒收到 token，開始重試
    setTimeout(() => {
      // 🔧 移除 parentOrigin 檢查，因為 referrer 可能為空（安全限制）
      if (this.token === null && this.retryCount < this.maxRetries) {
        console.log('[AuthManager] 🔄 3秒未收到 token，開始重試發送 IFRAME_READY');
        this.retrySignalReadiness();
      }
    }, 3000);
  }

  private retrySignalReadiness(): void {
    // 🔧 移除 parentOrigin 檢查
    if (this.token !== null || this.retryCount >= this.maxRetries) {
      return; // 已經收到 token 或達到最大重試次數
    }

    this.retryCount++;
    // 重試時重置 readinessSignaled，允許重新發送
    this.readinessSignaled = false;
    
    // 🔴 強制輸出
    console.log(`[AuthManager] 🔄 重試 ${this.retryCount}/${this.maxRetries} 次發送 IFRAME_READY`);
    
    this.signalReadiness();

    // 繼續重試
    if (this.retryCount < this.maxRetries) {
      this.retryTimer = window.setTimeout(() => {
        this.retrySignalReadiness();
      }, this.retryInterval);
    } else {
      if (shouldLog('warn')) {
        console.warn('[AuthManager] 達到最大重試次數，停止重試');
      }
    }
  }

  private stopRetryMechanism(): void {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.timeoutTimer) {
      clearTimeout(this.timeoutTimer);
      this.timeoutTimer = null;
    }
    // 清理 DOMContentLoaded 事件監聽器
    if (this.domContentLoadedHandler) {
      document.removeEventListener('DOMContentLoaded', this.domContentLoadedHandler);
      this.domContentLoadedHandler = null;
    }
  }

  private setupMessageListener(): void {
    if (shouldLog('debug')) {
      console.log(`[AuthManager] Setting up message listener, parentOrigin: ${this.parentOrigin}`);
    }

    window.addEventListener('message', (event: MessageEvent) => {
      if (shouldLog('debug')) {
        console.log(`[AuthManager] Received message from origin: ${event.origin}, data:`, event.data);
      }

      // 安全檢查：驗證訊息來源
      // 如果無法推斷 parent origin，在開發環境中允許接收訊息
      if (this.parentOrigin && event.origin !== this.parentOrigin) {
        if (shouldLog('warn')) {
          console.warn(`[AuthManager] 收到來自未授權來源的訊息：${event.origin}，預期來源：${this.parentOrigin}`);
        }
        return;
      }

      // 如果 parentOrigin 為 null，記錄警告但允許處理（開發環境）
      if (!this.parentOrigin) {
        if (shouldLog('warn')) {
          console.warn(`[AuthManager] 無法推斷 parent origin，允許接收來自 ${event.origin} 的訊息（開發模式）`);
        }
      }

      const { data } = event;
      if (shouldLog('debug')) {
        console.log(`[AuthManager] Origin check passed, processing data:`, data);
      }

      // 安全檢查：驗證資料結構和內容
      if (this.isValidTokenMessage(data)) {
        if (shouldLog('debug')) {
          console.log(`[AuthManager] Valid token message received, setting token:`, data.token);
        }
        this.setToken(data.token);
      } else if (this.isValidDomainConfigMessage(data)) {
        // 🔴 強制輸出，用於 debug
        console.log(`[AuthManager] 📥 收到 Domain Config:`, {
          iconBaseUrl: data.iconBaseUrl,
          apiUrlPrefix: data.apiUrlPrefix,
          rawData: data,
        });
        this.handleDomainConfig(data);
      } else {
        if (shouldLog('warn')) {
          console.warn(`[AuthManager] Invalid token message format:`, data);
        }
        this.handleMalformedMessage(data);
      }
    });
  }

  private isValidTokenMessage(data: unknown): data is TokenMessage {
    return (
      typeof data === 'object' &&
      data !== null &&
      'type' in data &&
      'token' in data &&
      (data as TokenMessage).type === 'SET_TOKEN' &&
      typeof (data as TokenMessage).token === 'string' &&
      (data as TokenMessage).token.length > 0
    );
  }

  private isValidDomainConfigMessage(data: unknown): data is DomainConfigMessage {
    if (typeof data !== 'object' || data === null) {
      return false;
    }

    const msg = data as DomainConfigMessage;
    
    // 支援兩種格式：
    // 1. 標準格式：{ type: 'SET_DOMAIN_CONFIG', ... }
    // 2. 父層實際發送的格式：{ iconBaseUrl: '...', apiUrlPrefix: '...' }（沒有 type）
    const hasType = 'type' in msg && msg.type === 'SET_DOMAIN_CONFIG';
    const hasDomainConfig = 
      ('iconBaseUrl' in msg && typeof msg.iconBaseUrl === 'string') ||
      ('apiUrlPrefix' in msg && typeof msg.apiUrlPrefix === 'string');
    
    return hasType || hasDomainConfig;
  }

  private handleMalformedMessage(data: unknown): void {
    if (shouldLog('warn')) {
      console.warn('收到格式錯誤的訊息：', {
        data,
        expectedType: 'SET_TOKEN or SET_DOMAIN_CONFIG',
        expectedFields: ['type', 'token'] + ' or ' + ['type', 'iconBaseUrl?', 'apiUrlPrefix?'],
      });
    }
  }

  private handleDomainConfig(data: DomainConfigMessage): void {
    updateDomainConfig(data.apiUrlPrefix, data.iconBaseUrl, data.publicUrlEncryptKeys);

    console.log('[AuthManager] ✅ Domain config 更新完成:', {
      apiBaseUrl: config.api.baseUrl,
      iconBaseUrl: config.assets.iconBaseUrl,
      isIconBaseUrlFromParent: config.assets.isIconBaseUrlFromParent,
      encryptKeyCount: config.assets.encryptKeyMap.size,
    });

    // 如果 BattlePassService 已經建立，需要用新的 API URL 重建
    if (this.battlePassService && this.token) {
      console.log('[AuthManager] 🔄 Domain config 更新，重建 BattlePassService');
      this.battlePassService = new BattlePassService(config.api.baseUrl, this.token);
    }
  }

  private setToken(token: string): void {
    this.token = token;

    // 解析並儲存用戶 ID
    this.userId = this.parseUserIdFromToken(token);

    // 停止重試機制和超時
    this.stopRetryMechanism();

    console.log('[AuthManager] 🔑 收到 Token，建立 BattlePassService:', {
      apiBaseUrl: config.api.baseUrl,
      iconBaseUrl: config.assets.iconBaseUrl,
      userId: this.userId,
    });

    // 使用當前 config 建立 BattlePassService（不管有沒有收到 domain config）
    this.battlePassService = new BattlePassService(config.api.baseUrl, token);

    // 通知所有訂閱者
    this.notifyTokenChange();
  }

  public getToken(): string | null {
    return this.token;
  }

  public getUserId(): string | null {
    return this.userId;
  }

  /**
   * 從 JWT token 中解析用戶 ID
   * @param token JWT token
   * @returns 用戶 ID 或 null
   */
  private parseUserIdFromToken(token: string): string | null {
    try {
      // JWT token 格式：header.payload.signature
      const parts = token.split('.');
      if (parts.length !== 3) {
        if (shouldLog('warn')) {
          console.warn('[AuthManager] JWT token 格式錯誤');
        }
        return null;
      }

      // 解碼 payload (base64url)
      const payload = parts[1];
      // 將 base64url 轉換為 base64
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      // 補齊 padding
      const paddedBase64 = base64 + '='.repeat((4 - base64.length % 4) % 4);
      
      const decodedPayload = JSON.parse(atob(paddedBase64));
      
      // 從 'sub' 欄位提取用戶 ID
      const userId = decodedPayload.sub;
      
      if (typeof userId === 'string' && userId.length > 0) {
        if (shouldLog('info')) {
          console.log('[AuthManager] 成功解析用戶 ID:', userId);
        }
        return userId;
      } else {
        if (shouldLog('warn')) {
          console.warn('[AuthManager] JWT token 中沒有有效的用戶 ID');
        }
        return null;
      }
    } catch (error) {
      if (shouldLog('error')) {
        console.error('[AuthManager] 解析 JWT token 失敗:', error);
      }
      return null;
    }
  }

  public getBattlePassService(): BattlePassService | null {
    return this.battlePassService;
  }

  public onTokenChange(callback: (_token: string | null) => void): void {
    this.tokenChangeCallbacks.push(callback);
  }

  private notifyTokenChange(): void {
    this.tokenChangeCallbacks.forEach(callback => {
      try {
        callback(this.token);
      } catch (error) {
        if (shouldLog('error')) {
          console.error('Token change callback 執行錯誤：', error);
        }
      }
    });
  }

  public isAuthenticated(): boolean {
    // 離線模式（沒有 parent origin）視為已認證
    if (!this.parentOrigin) {
      return true;
    }
    return this.token !== null;
  }

  public clearToken(): void {
    this.token = null;
    this.battlePassService = null;
    this.readinessSignaled = false;
    this.retryCount = 0;
    this.iframeReadySendCount = 0; // 重置發送計數
    
    // 停止重試機制和超時
    this.stopRetryMechanism();
    
    this.notifyTokenChange();
    if (shouldLog('info')) {
      console.log('Token 已清除');
    }
  }

  public getParentOrigin(): string | null {
    return this.parentOrigin;
  }
}
